import { Plus, Minus } from 'lucide-react';
import { Controller, useFormContext } from 'react-hook-form';

import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';

type Props = {
  name: string;
  /** Légende sous le champ */
  captionText?: string;
  min?: number;
  max?: number;
  className?: string;
};

/** Champ numérique avec boutons − / + (remplace `Field.NumberInput` de Minimal). */
export const RHFNumberInput = ({ name, captionText, min = 0, max = Number.POSITIVE_INFINITY, className }: Props) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const value = Number(field.value) || 0;
        const clamp = (next: number) => Math.min(max, Math.max(min, next));

        return (
          <div className={className}>
            <div className="flex items-center justify-center gap-2">
              <Button
                aria-label="Diminuer"
                disabled={value <= min}
                onClick={() => field.onChange(clamp(value - 1))}
                size="icon"
                type="button"
                variant="outline"
              >
                <Minus />
              </Button>
              <Input
                aria-invalid={error ? true : undefined}
                className="w-20 text-center"
                inputMode="numeric"
                max={max === Number.POSITIVE_INFINITY ? undefined : max}
                min={min}
                name={field.name}
                onBlur={field.onBlur}
                onChange={(event) => field.onChange(event.target.value === '' ? '' : clamp(Number(event.target.value)))}
                ref={field.ref}
                size="sm"
                type="number"
                value={field.value ?? ''}
              />
              <Button
                aria-label="Augmenter"
                disabled={value >= max}
                onClick={() => field.onChange(clamp(value + 1))}
                size="icon"
                type="button"
                variant="outline"
              >
                <Plus />
              </Button>
            </div>
            {(captionText || error?.message) && (
              <p
                className={
                  error ? 'mt-1 text-center text-xs text-destructive' : 'mt-1 text-center text-xs text-muted-foreground'
                }
              >
                {error?.message ?? captionText}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};
